import type { NavLeaf } from './types';

const COOKIE_MAX_AGE_SEC = 60 * 60 * 24 * 365;

export interface NavMenuState {
  expandedIds: string[];
  searchOpen?: boolean;
}

export interface NavCommandItem {
  id: string;
  text: string;
  path: string;
  breadcrumb: string;
  onSelect?: () => void;
}

function isBrowser(): boolean {
  return typeof document !== 'undefined';
}

export function parseExpandedIds(raw: string[] | undefined | null): Set<string> {
  if (!raw?.length) return new Set();
  return new Set(raw.filter((id) => typeof id === 'string' && id.length > 0));
}

export function readNavMenuState(cookieName: string): NavMenuState {
  if (!isBrowser()) return { expandedIds: [] };
  try {
    const match = document.cookie
      .split(';')
      .map((part) => part.trim())
      .find((part) => part.startsWith(`${cookieName}=`));
    if (!match) return { expandedIds: [] };
    const encoded = match.slice(cookieName.length + 1);
    const parsed = JSON.parse(decodeURIComponent(encoded)) as Partial<NavMenuState>;
    return {
      expandedIds: Array.isArray(parsed.expandedIds)
        ? parsed.expandedIds.filter((id): id is string => typeof id === 'string')
        : [],
      searchOpen: parsed.searchOpen === true
    };
  } catch {
    return { expandedIds: [] };
  }
}

export function writeNavMenuState(cookieName: string, state: NavMenuState): void {
  if (!isBrowser()) return;
  try {
    const payload = encodeURIComponent(
      JSON.stringify({
        expandedIds: [...new Set(state.expandedIds)],
        searchOpen: state.searchOpen === true
      })
    );
    document.cookie = `${cookieName}=${payload}; path=/; max-age=${COOKIE_MAX_AGE_SEC}; SameSite=Lax`;
  } catch {
    /* cookies may be disabled */
  }
}

export function applyExpandedState<TItem extends NavLeaf>(
  items: TItem[],
  expandedIds: Set<string>,
  ancestorIdsForPath?: Set<string>
): TItem[] {
  const effective = new Set([...expandedIds, ...(ancestorIdsForPath ?? [])]);
  return items.map((node) => {
    const kids = node.items ? applyExpandedState(node.items as TItem[], expandedIds, ancestorIdsForPath) : undefined;
    const hasChildren = Boolean(kids?.length);
    return {
      ...node,
      items: kids,
      expanded: hasChildren ? effective.has(node.id) : undefined
    };
  });
}

export function collectDescendantIds(node: NavLeaf): string[] {
  const ids: string[] = [];
  if (node.items) {
    for (const child of node.items) {
      ids.push(child.id);
      ids.push(...collectDescendantIds(child));
    }
  }
  return ids;
}

/** Ids of every branch node (has children) in the visible nav tree. */
export function collectExpandableNodeIds(items: NavLeaf[]): string[] {
  const ids: string[] = [];
  for (const node of items) {
    if (node.items?.length) {
      ids.push(node.id);
      ids.push(...collectExpandableNodeIds(node.items));
    }
  }
  return ids;
}

export function collapseNavToRouteAncestors(ancestorIds: Set<string>): Set<string> {
  return new Set(ancestorIds);
}

export function expandAllNavNodes(items: NavLeaf[]): Set<string> {
  return new Set(collectExpandableNodeIds(items));
}

export function findAncestorIdsForPath(items: NavLeaf[], pathname: string): Set<string> {
  const normalized = normalizePath(pathname);

  function walk(nodes: NavLeaf[], ancestors: string[]): string[] | null {
    for (const node of nodes) {
      if (node.path !== undefined && normalizePath(node.path) === normalized) {
        return ancestors;
      }
      if (node.items?.length) {
        const found = walk(node.items, [...ancestors, node.id]);
        if (found) return found;
      }
    }
    return null;
  }

  return new Set(walk(items, []) ?? []);
}

export function flattenNavItems(items: NavLeaf[], parents: string[] = []): NavCommandItem[] {
  const flat: NavCommandItem[] = [];
  for (const node of items) {
    const trail = [...parents, node.text];
    if (node.path || node.onSelect) {
      flat.push({
        id: node.id,
        text: node.text,
        path: node.path ?? '',
        breadcrumb: parents.join(' › '),
        onSelect: node.onSelect
      });
    }
    if (node.items?.length) {
      flat.push(...flattenNavItems(node.items, trail));
    }
  }
  return flat;
}

export function filterNavCommands(flat: NavCommandItem[], query: string): NavCommandItem[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return flat.filter(
    (item) =>
      item.text.toLowerCase().includes(q) ||
      item.breadcrumb.toLowerCase().includes(q) ||
      `${item.breadcrumb} ${item.text}`.toLowerCase().includes(q)
  );
}

export function removeIdsFromExpandedSet(expandedIds: Set<string>, idsToRemove: Iterable<string>): Set<string> {
  const next = new Set(expandedIds);
  for (const id of idsToRemove) next.delete(id);
  return next;
}

function normalizePath(path: string): string {
  if (path.length > 1 && path.endsWith('/')) return path.slice(0, -1);
  return path;
}
